import { useMemo, useState } from 'react'
import { Card, Badge, Table, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { GET_ALL_CHANNEL_CONFIGS } from '../../../gql/queries/channelConfigAdminQuery'
import { ChannelMessagesModal } from './ChannelMessagesModal'
import { URL as BACKEND_URL } from '../../../gql/client'

type ChannelKind = 'SMS' | 'WHATSAPP' | 'EMAIL'

interface ChannelRow {
    ownerId: string
    ownerType: string
    ownerLabel: string
    channelKind: ChannelKind
    displayNumber: string
    sidMasked?: string | null
    webhookPath?: string | null
    createdAt?: string | null
    issues: string[]
    isFirstOfOwner: boolean
    ownerChannelCount: number
}

const CHANNEL_KIND_LABEL: Record<ChannelKind, string> = {
    SMS: 'SMS',
    WHATSAPP: 'WhatsApp',
    EMAIL: 'Email',
}

const SECTION_ORDER: { ownerType: string; title: string }[] = [
    { ownerType: 'organization', title: 'Organization' },
    { ownerType: 'group', title: 'Assignment Groups' },
    { ownerType: 'user', title: 'Users' },
]

const buildChannelRows = (allChannelConfigs: any[]): ChannelRow[] => {
    const rows: ChannelRow[] = []

    allChannelConfigs.forEach((owner: any) => {
        const ownerChannels: Omit<ChannelRow, 'isFirstOfOwner' | 'ownerChannelCount'>[] = []

        if (owner.twilioEnabled && owner.twilioPhoneNumber) {
            ownerChannels.push({
                ownerId: owner.ownerId,
                ownerType: owner.ownerType,
                ownerLabel: owner.ownerLabel,
                channelKind: 'SMS',
                displayNumber: owner.twilioPhoneNumber,
                sidMasked: owner.twilioAccountSidMasked,
                webhookPath: (owner.webhookPaths || []).find((p: string) => p.includes('/sms/')) || null,
                createdAt: owner.createdAt || null,
                issues: owner.issues || [],
            })
        }

        if (owner.whatsappProvider && owner.whatsappProvider !== 'NONE') {
            ownerChannels.push({
                ownerId: owner.ownerId,
                ownerType: owner.ownerType,
                ownerLabel: owner.ownerLabel,
                channelKind: 'WHATSAPP',
                displayNumber: owner.whatsappNumber || '—',
                sidMasked: owner.whatsappAccountSidMasked,
                webhookPath: (owner.webhookPaths || []).find((p: string) => p.includes('/whatsapp/')) || null,
                createdAt: owner.createdAt || null,
                issues: owner.issues || [],
            })
        }

        if (owner.emailEnabled && owner.emailFrom) {
            ownerChannels.push({
                ownerId: owner.ownerId,
                ownerType: owner.ownerType,
                ownerLabel: owner.ownerLabel,
                channelKind: 'EMAIL',
                displayNumber: owner.emailFrom,
                sidMasked: null,
                webhookPath: null,
                createdAt: owner.createdAt || null,
                issues: owner.issues || [],
            })
        }

        ownerChannels.forEach((row, idx) => {
            rows.push({
                ...row,
                isFirstOfOwner: idx === 0,
                ownerChannelCount: ownerChannels.length,
            })
        })
    })

    return rows
}

const OWNER_BADGE: Record<string, { label: string; variant: string; textClass: string }> = {
    organization: { label: 'Organization', variant: 'primary', textClass: 'text-white' },
    group: { label: 'Group', variant: 'info', textClass: 'text-white' },
    // Metronic's "secondary" is a light gray ($gray-300), so it needs dark text for contrast.
    user: { label: 'User', variant: 'secondary', textClass: 'text-dark' },
}

const ISSUE_LABELS: Record<string, string> = {
    duplicate_number: 'This phone number is configured in more than one channel',
    sid_mismatch: 'The same number appears with different Account SIDs across configs',
}

interface OrganizationChannelsTableProps {
    // Already-decoded numeric-string organization id (NOT a relay-encoded id).
    organizationId?: string
}

interface OwnerGroup {
    ownerId: string
    ownerType: string
    ownerLabel: string
    channels: ChannelRow[]
}

const buildOwnerGroups = (allChannelConfigs: any[]): Record<string, OwnerGroup[]> => {
    const channelRows = buildChannelRows(allChannelConfigs)
    const bySection: Record<string, OwnerGroup[]> = { organization: [], group: [], user: [] }

    const ownersMap: Record<string, OwnerGroup> = {}

    channelRows.forEach((row) => {
        const key = `${row.ownerType}-${row.ownerId}`
        if (!ownersMap[key]) {
            ownersMap[key] = {
                ownerId: row.ownerId,
                ownerType: row.ownerType,
                ownerLabel: row.ownerLabel,
                channels: [],
            }
            if (bySection[row.ownerType]) {
                bySection[row.ownerType].push(ownersMap[key])
            }
        }
        ownersMap[key].channels.push(row)
    })

    return bySection
}

const getChannelIcon = (kind: ChannelKind) => {
    switch (kind) {
        case 'WHATSAPP':
            return <i className="bi bi-whatsapp text-success fs-5 me-2" />
        case 'SMS':
            return <i className="bi bi-chat-dots-fill text-primary fs-5 me-2" />
        case 'EMAIL':
            return <i className="bi bi-envelope-fill text-info fs-5 me-2" />
    }
}

const getOwnerIcon = (ownerType: string) => {
    switch (ownerType) {
        case 'organization':
            return <i className="bi bi-building fs-4 me-2 text-primary" />
        case 'group':
            return <i className="bi bi-people-fill fs-4 me-2 text-info" />
        case 'user':
            return <i className="bi bi-person-circle fs-4 me-2 text-gray-600" />
    }
}

export const OrganizationChannelsTable = ({ organizationId }: OrganizationChannelsTableProps) => {
    const navigate = useNavigate()
    const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')

    const { data, loading } = useQuery(GET_ALL_CHANNEL_CONFIGS, {
        variables: { organizationId },
        fetchPolicy: 'network-only',
        skip: !organizationId,
    })
    const allChannelConfigs = data?.allChannelConfigs || []
    const ownerGroupsBySection = useMemo(() => buildOwnerGroups(allChannelConfigs), [allChannelConfigs])
    const channelRows = useMemo(() => buildChannelRows(allChannelConfigs), [allChannelConfigs])
    const channelRowsBySection = useMemo(() => {
        const bySection: Record<string, ChannelRow[]> = { organization: [], group: [], user: [] }
        channelRows.forEach((row) => {
            if (bySection[row.ownerType]) {
                bySection[row.ownerType].push(row)
            }
        })
        return bySection
    }, [channelRows])

    const [messagesModalRow, setMessagesModalRow] = useState<ChannelRow | null>(null)

    const handleCopyWebhookPath = (path: string) => {
        navigator.clipboard.writeText(`${BACKEND_URL}${path}`)
        toast.success('Webhook URL copied')
    }

    const handleEditChannelOwner = (row: any) => {
        if (row.ownerType === 'user') {
            navigate(`/user/${row.ownerId}`)
        } else if (row.ownerType === 'group') {
            navigate('/assignment-groups')
        }
    }

    if (loading) {
        return (
            <div className="text-center py-10">
                <Spinner animation="border" />
            </div>
        )
    }

    return (
        <>
            {/* View Mode Toggle Header */}
            <div className="d-flex justify-content-end align-items-center mb-4">
                <div className="btn-group btn-group-sm bg-light p-1 rounded border">
                    <button
                        type="button"
                        className={`btn btn-sm ${viewMode === 'cards' ? 'btn-white shadow-sm fw-bold' : 'btn-light text-muted'}`}
                        onClick={() => setViewMode('cards')}
                    >
                        <i className="bi bi-grid me-1" /> Cards
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${viewMode === 'table' ? 'btn-white shadow-sm fw-bold' : 'btn-light text-muted'}`}
                        onClick={() => setViewMode('table')}
                    >
                        <i className="bi bi-table me-1" /> Table
                    </button>
                </div>
            </div>

            {SECTION_ORDER.map((section) => {
                const ownerGroups = ownerGroupsBySection[section.ownerType] || []
                const rows = channelRowsBySection[section.ownerType] || []
                const ownerBadge = OWNER_BADGE[section.ownerType]

                return (
                    <div className="mb-6" key={section.ownerType}>
                        <div className="d-flex align-items-center mb-3">
                            <Badge bg={ownerBadge?.variant || 'secondary'} className={`me-2 ${ownerBadge?.textClass || 'text-dark'}`}>
                                {ownerBadge?.label || section.title}
                            </Badge>
                            <h4 className="fw-bold mb-0 text-gray-800">{section.title}</h4>
                            <span className="badge bg-light text-muted ms-2 fs-8">
                                {ownerGroups.length} {ownerGroups.length === 1 ? 'owner' : 'owners'} • {rows.length} {rows.length === 1 ? 'channel' : 'channels'}
                            </span>
                        </div>

                        {ownerGroups.length === 0 ? (
                            <Card className="shadow-sm border-0 mb-4 bg-light-subtle">
                                <Card.Body className="text-center text-muted py-5">
                                    <i className="bi bi-inbox fs-2 d-block mb-2 text-muted" />
                                    No channels configured in this section.
                                </Card.Body>
                            </Card>
                        ) : viewMode === 'cards' ? (
                            /* ── Cards View (Option A) ── */
                            <div className="d-flex flex-column gap-3 mb-4">
                                {ownerGroups.map((owner) => (
                                    <Card key={`${owner.ownerType}-${owner.ownerId}`} className="shadow-sm border border-gray-200">
                                        <Card.Header className="bg-light py-3 px-4 d-flex align-items-center justify-content-between border-bottom">
                                            <div className="d-flex align-items-center">
                                                {getOwnerIcon(owner.ownerType)}
                                                <div>
                                                    <span className="fw-bold fs-6 text-gray-900">{owner.ownerLabel}</span>
                                                    <Badge bg={ownerBadge?.variant || 'secondary'} className={`ms-2 ${ownerBadge?.textClass || 'text-dark'} fs-9`}>
                                                        {ownerBadge?.label}
                                                    </Badge>
                                                </div>
                                            </div>
                                            {owner.ownerType !== 'organization' && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline btn-outline-primary py-1 px-3"
                                                    onClick={() => handleEditChannelOwner(owner)}
                                                >
                                                    <i className="bi bi-pencil-square me-1" /> Edit
                                                </button>
                                            )}
                                        </Card.Header>

                                        <Card.Body className="p-0">
                                            {owner.channels.map((channel, idx) => (
                                                <div
                                                    key={`${channel.ownerId}-${channel.channelKind}-${idx}`}
                                                    className={`p-4 d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 ${idx < owner.channels.length - 1 ? 'border-bottom' : ''}`}
                                                >
                                                    {/* Left: Channel info */}
                                                    <div style={{ minWidth: '260px' }}>
                                                        <div className="d-flex align-items-center mb-1">
                                                            {getChannelIcon(channel.channelKind)}
                                                            <span className="fw-bold fs-6 text-gray-900 me-2">
                                                                {channel.displayNumber}
                                                            </span>
                                                            <Badge bg="light" text="dark" className="border fs-9">
                                                                {CHANNEL_KIND_LABEL[channel.channelKind]}
                                                            </Badge>
                                                        </div>
                                                        <div className="text-muted fs-8 d-flex flex-wrap gap-3">
                                                            {channel.sidMasked && (
                                                                <span><strong className="text-gray-600">SID:</strong> {channel.sidMasked}</span>
                                                            )}
                                                            {channel.createdAt && (
                                                                <span>
                                                                    <i className="bi bi-calendar-event me-1" />
                                                                    {new Date(channel.createdAt).toLocaleDateString()} {new Date(channel.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Middle: Webhook */}
                                                    <div className="flex-grow-1" style={{ maxWidth: '550px' }}>
                                                        {channel.webhookPath ? (
                                                            <div className="d-flex align-items-center bg-light rounded px-3 py-2 border">
                                                                <span className="text-muted fs-9 fw-bold me-2">WEBHOOK</span>
                                                                <code className="fs-8 text-break text-dark flex-grow-1">{channel.webhookPath}</code>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-icon btn-white border shadow-sm ms-2"
                                                                    style={{ width: '28px', height: '28px' }}
                                                                    title="Copy full URL"
                                                                    onClick={() => handleCopyWebhookPath(channel.webhookPath as string)}
                                                                >
                                                                    <i className="bi bi-clipboard fs-8" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted fs-8">—</span>
                                                        )}
                                                    </div>

                                                    {/* Right: Issues & Actions */}
                                                    <div className="d-flex align-items-center gap-2 justify-content-end" style={{ minWidth: '180px' }}>
                                                        {channel.issues?.length > 0 && (
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {channel.issues.map((issue: string) => ISSUE_LABELS[issue] || issue).join(' · ')}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <Badge bg="warning" text="dark" className="d-flex align-items-center py-2 px-3">
                                                                    <i className="bi bi-exclamation-triangle-fill me-1" /> Review
                                                                </Badge>
                                                            </OverlayTrigger>
                                                        )}

                                                        <OverlayTrigger
                                                            overlay={
                                                                channel.channelKind === 'EMAIL' ? (
                                                                    <Tooltip>Message history is not available for Email yet.</Tooltip>
                                                                ) : (
                                                                    <Tooltip>View message history for this channel.</Tooltip>
                                                                )
                                                            }
                                                        >
                                                            <span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-light-info fw-semibold"
                                                                    disabled={channel.channelKind === 'EMAIL'}
                                                                    onClick={() => setMessagesModalRow(channel)}
                                                                >
                                                                    <i className="bi bi-chat-left-text me-1" /> View messages
                                                                </button>
                                                            </span>
                                                        </OverlayTrigger>
                                                    </div>
                                                </div>
                                            ))}
                                        </Card.Body>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            /* ── Table View ── */
                            <Card className="shadow-sm mb-4">
                                <Card.Body className="py-3">
                                    <div className="table-responsive">
                                        <Table hover className="align-middle">
                                            <thead>
                                                <tr className="text-muted fw-bold fs-7 text-uppercase">
                                                    <th style={{ width: '22%', minWidth: '180px' }}>User Information</th>
                                                    <th style={{ width: '28%' }}>Channel</th>
                                                    <th style={{ width: '30%' }}>Webhook</th>
                                                    <th style={{ width: '8%' }}>Info</th>
                                                    <th style={{ width: '12%' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((row) => (
                                                    <tr key={`${row.ownerId}-${row.channelKind}`}>
                                                        {row.isFirstOfOwner && (
                                                            <td rowSpan={row.ownerChannelCount} className="fw-semibold text-gray-800" style={{ wordBreak: 'break-word' }}>
                                                                {row.ownerLabel}
                                                            </td>
                                                        )}
                                                        <td>
                                                            <div className="d-flex align-items-center mb-1">
                                                                {getChannelIcon(row.channelKind)}
                                                                <Badge bg="light" text="dark" className="border me-1">
                                                                    {CHANNEL_KIND_LABEL[row.channelKind]}
                                                                </Badge>
                                                            </div>
                                                            <div className="fw-bold text-gray-800">{row.displayNumber}</div>
                                                            {row.sidMasked && (
                                                                <div className="text-muted fs-8">SID {row.sidMasked}</div>
                                                            )}
                                                            {row.createdAt && (
                                                                <div className="text-muted fs-8 mt-1">
                                                                    <i className="bi bi-calendar-event me-1" />
                                                                    {new Date(row.createdAt).toLocaleDateString()} {new Date(row.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {row.webhookPath ? (
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <code className="fs-8 text-break">{row.webhookPath}</code>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-icon btn-light p-1"
                                                                        style={{ minWidth: '22px', height: '22px' }}
                                                                        title="Copy full URL"
                                                                        onClick={() => handleCopyWebhookPath(row.webhookPath as string)}
                                                                    >
                                                                        <i className="bi bi-clipboard fs-8" />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">—</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {row.issues?.length > 0 && (
                                                                <OverlayTrigger
                                                                    overlay={
                                                                        <Tooltip>
                                                                            {row.issues.map((issue: string) => ISSUE_LABELS[issue] || issue).join(' · ')}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <Badge bg="warning" text="dark">
                                                                        <i className="bi bi-exclamation-triangle-fill me-1" /> Review
                                                                    </Badge>
                                                                </OverlayTrigger>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-2 justify-content-end">
                                                                {row.ownerType !== 'organization' && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-light-primary"
                                                                        onClick={() => handleEditChannelOwner(row)}
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                )}
                                                                <OverlayTrigger
                                                                    overlay={
                                                                        row.channelKind === 'EMAIL' ? (
                                                                            <Tooltip>Message history is not available for Email yet.</Tooltip>
                                                                        ) : (
                                                                            <Tooltip>View message history for this channel.</Tooltip>
                                                                        )
                                                                    }
                                                                >
                                                                    <span>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-light-info"
                                                                            disabled={row.channelKind === 'EMAIL'}
                                                                            onClick={() => setMessagesModalRow(row)}
                                                                        >
                                                                            View messages
                                                                        </button>
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}
                    </div>
                )
            })}

            <ChannelMessagesModal
                isOpen={!!messagesModalRow}
                onClose={() => setMessagesModalRow(null)}
                phoneNumber={messagesModalRow?.displayNumber}
                channelKind={messagesModalRow?.channelKind}
                ownerLabel={messagesModalRow?.ownerLabel}
            />
        </>
    )
}
